import { useEffect, useState } from 'react';
import { FEATURED_MARKET_CODES } from '../../config/constants';
import { getCountry } from '../../data/countries';
import {
  buildSubmissionContentApiBody,
  filterNumericInput,
  formatSubmissionStatusLabel,
  getSubmissionListingMode,
  normalizePipelineStatus,
  submissionContentFormFromRecord,
} from '../../utils/submissions';
import AdminSubmissionExtrasFields from './AdminSubmissionExtrasFields';
import ListingDetailsToggle from './ListingDetailsToggle';
import NearbyAmenitiesFields from '../forms/NearbyAmenitiesFields';

const SECTIONS = [
  { id: 'basics', icon: 'home', title: 'Basics', subtitle: 'Core property information', iconBg: '#fef3c7' },
  { id: 'details', icon: 'ruler-combined', title: 'Property', subtitle: 'Specs and financials', iconBg: '#dbeafe' },
  { id: 'financials', icon: 'chart-line', title: 'Financials', subtitle: 'Lister inputs and CoOwn-reviewed figures', iconBg: '#cffafe', saleOnly: true, wide: true },
  { id: 'nearby', icon: 'map-marker-alt', title: 'Nearby', subtitle: 'Travel times to key locations', iconBg: '#fce7f3', wide: true },
  { id: 'extras', icon: 'receipt', title: 'Costs & extras', subtitle: 'Monthly cost breakdown and options', iconBg: '#d1fae5', rentOnly: true, wide: true },
  { id: 'reno', icon: 'tools', title: 'Renovation', subtitle: 'Renovation and land banking options', iconBg: '#fef3c7', saleOnly: true, wide: true },
  { id: 'agent', icon: 'user-tie', title: 'Agent', subtitle: 'Listing contact information', iconBg: '#ede9fe', wide: true },
];

const AdminSubmissionContentEditor = ({ sub, onSave, saving, addToast }) => {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(() => submissionContentFormFromRecord(sub));

  useEffect(() => {
    setForm(submissionContentFormFromRecord(sub));
  }, [sub?.id, sub?.updatedAt, sub?.title, sub?.agentEmail]);

  const update = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));
  const isRent = getSubmissionListingMode(sub) === 'rent';
  const pipelineLabel = formatSubmissionStatusLabel(sub?.status);
  const isLive = normalizePipelineStatus(sub?.status) === 'live';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim() || !form.location?.trim()) {
      addToast?.('Title and location are required.', 'error');
      return;
    }
    if (!form.agentEmail?.trim()) {
      addToast?.('Agent email is required.', 'error');
      return;
    }
    await onSave?.(buildSubmissionContentApiBody(form, sub));
  };

  const visibleSections = SECTIONS.filter((s) => {
    if (s.rentOnly && !isRent) return false;
    if (s.saleOnly && isRent) return false;
    return true;
  });

  return (
    <form className="admin-submission-content admin-luxury-collapsible" onSubmit={handleSubmit}>
      <header className="admin-submission-content__head">
        <button
          type="button"
          className="admin-submission-content__toggle"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
        >
          <div className="admin-submission-content__head-main">
            <div className="admin-submission-content__head-icon" aria-hidden="true">
              <i className="fas fa-pen-fancy"></i>
            </div>
            <div>
              <h3>Listing details</h3>
              <p>Edit property facts, agent contact, and rent breakdown — same fields the lister submitted.</p>
            </div>
          </div>
          <div className="admin-submission-content__head-meta">
            <span className={`admin-submission-content__pipeline-badge${isLive ? ' admin-submission-content__pipeline-badge--live' : ''}`}>
              {isLive ? <span className="admin-submission-content__pipeline-dot" aria-hidden="true" /> : null}
              {pipelineLabel}
            </span>
            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} admin-submission-content__chevron`} aria-hidden="true"></i>
          </div>
        </button>
      </header>

      {expanded ? (
        <>
          <div className="admin-submission-content__grid">
            {visibleSections.map((section) => (
              <section
                key={section.id}
                className={`admin-submission-content__card${section.wide ? ' admin-submission-content__card--wide' : ''}`}
              >
                <div className="admin-submission-content__section-header">
                  <div
                    className="admin-submission-content__section-icon"
                    style={{ background: section.iconBg }}
                    aria-hidden="true"
                  >
                    <i className={`fas fa-${section.icon}`}></i>
                  </div>
                  <div>
                    <h4 className="admin-submission-content__section-title">{section.title}</h4>
                    {section.subtitle ? (
                      <p className="admin-submission-content__section-subtitle">{section.subtitle}</p>
                    ) : null}
                  </div>
                </div>

                {section.id === 'basics' ? (
                  <div className="admin-submission-content__fields">
                    <div className="admin-submission-content__row">
                      <label className="admin-submission-content__field">
                        <span>Title</span>
                        <input required value={form.title} onChange={(e) => update('title', e.target.value)} />
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Type</span>
                        <select value={form.type} onChange={(e) => update('type', e.target.value)}>
                          <option>Residential</option>
                          <option>Commercial</option>
                          <option>Vacation</option>
                          <option>Land</option>
                        </select>
                      </label>
                    </div>
                    <label className="admin-submission-content__field admin-submission-content__field--full">
                      <span>Location</span>
                      <input required value={form.location} onChange={(e) => update('location', e.target.value)} />
                    </label>
                    <div className="admin-submission-content__row">
                      <label className="admin-submission-content__field">
                        <span>Country</span>
                        <select value={form.country} onChange={(e) => update('country', e.target.value)}>
                          {FEATURED_MARKET_CODES.map((code) => (
                            <option key={code} value={code}>{getCountry(code).name}</option>
                          ))}
                        </select>
                      </label>
                      <label className="admin-submission-content__field">
                        <span>{isRent ? 'Property value ($)' : 'Lister ask ($)'}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form.price}
                          onChange={(e) => update('price', filterNumericInput(e.target.value, { decimal: true }))}
                        />
                      </label>
                    </div>
                    <label className="admin-submission-content__field admin-submission-content__field--full">
                      <span>Description</span>
                      <textarea rows={3} value={form.description} onChange={(e) => update('description', e.target.value)} />
                    </label>
                  </div>
                ) : null}

                {section.id === 'details' ? (
                  <div className="admin-submission-content__fields">
                    <div className="admin-submission-content__row admin-submission-content__row--quad">
                      <label className="admin-submission-content__field admin-submission-content__field--compact">
                        <span>Beds</span>
                        <input type="text" inputMode="numeric" value={form.beds} onChange={(e) => update('beds', filterNumericInput(e.target.value))} />
                      </label>
                      <label className="admin-submission-content__field admin-submission-content__field--compact">
                        <span>Baths</span>
                        <input type="text" inputMode="numeric" value={form.baths} onChange={(e) => update('baths', filterNumericInput(e.target.value))} />
                      </label>
                      <label className="admin-submission-content__field admin-submission-content__field--compact">
                        <span>Sq ft</span>
                        <input type="text" inputMode="numeric" value={form.sqft} onChange={(e) => update('sqft', filterNumericInput(e.target.value))} />
                      </label>
                      <label className="admin-submission-content__field admin-submission-content__field--compact">
                        <span>Year</span>
                        <input type="text" inputMode="numeric" value={form.yearBuilt} onChange={(e) => update('yearBuilt', filterNumericInput(e.target.value))} />
                      </label>
                    </div>
                    <div className="admin-submission-content__row">
                      <label className="admin-submission-content__field">
                        <span>Base monthly rent</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form.monthlyRent}
                          onChange={(e) => update('monthlyRent', filterNumericInput(e.target.value, { decimal: true }))}
                        />
                        <small>Required for rent · optional for sale</small>
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Occupancy rate (%)</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form.occupancy}
                          onChange={(e) => update('occupancy', filterNumericInput(e.target.value, { decimal: true }))}
                        />
                      </label>
                    </div>
                    <label className="admin-submission-content__field admin-submission-content__field--full">
                      <span>Est. annual operating expenses ($)</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form.annualOperatingExpenses}
                        onChange={(e) => update('annualOperatingExpenses', filterNumericInput(e.target.value, { decimal: true }))}
                      />
                    </label>
                  </div>
                ) : null}

                {section.id === 'financials' ? (
                  <div className="admin-submission-content__fields">
                    <p className="admin-submission-content__hint">
                      Lister inputs (rent, occupancy, expenses) feed the public overview. CoOwn-reviewed figures appear on the Financials tab after you save them here.
                    </p>
                    <div className="admin-submission-content__row admin-submission-content__row--triple">
                      <label className="admin-submission-content__field">
                        <span>Monthly rent ($)</span>
                        <input type="text" inputMode="decimal" value={form.monthlyRent} onChange={(e) => update('monthlyRent', filterNumericInput(e.target.value, { decimal: true }))} />
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Occupancy (%)</span>
                        <input type="text" inputMode="decimal" value={form.occupancy} onChange={(e) => update('occupancy', filterNumericInput(e.target.value, { decimal: true }))} />
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Annual operating expenses ($)</span>
                        <input type="text" inputMode="decimal" value={form.annualOperatingExpenses} onChange={(e) => update('annualOperatingExpenses', filterNumericInput(e.target.value, { decimal: true }))} />
                      </label>
                    </div>
                    <p className="admin-submission-content__subheading">CoOwn reviewed (investor-facing)</p>
                    <div className="admin-submission-content__row admin-submission-content__row--triple">
                      <label className="admin-submission-content__field">
                        <span>Net operating income ($)</span>
                        <input type="text" inputMode="decimal" value={form.netOperatingIncome} onChange={(e) => update('netOperatingIncome', filterNumericInput(e.target.value, { decimal: true }))} />
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Cap rate (%)</span>
                        <input type="text" inputMode="decimal" value={form.capRate} onChange={(e) => update('capRate', filterNumericInput(e.target.value, { decimal: true }))} />
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Projected return (%)</span>
                        <input type="text" inputMode="decimal" value={form.projectedReturn} onChange={(e) => update('projectedReturn', filterNumericInput(e.target.value, { decimal: true }))} />
                      </label>
                    </div>
                  </div>
                ) : null}

                {section.id === 'nearby' ? (
                  <div className="admin-submission-content__fields">
                    <p className="admin-submission-content__hint">
                      Travel times or distances shown on the property&apos;s Nearby tab. Leave blank if not applicable.
                    </p>
                    <NearbyAmenitiesFields form={form} update={update} variant="boxes" />
                  </div>
                ) : null}

                {section.id === 'extras' ? (
                  <AdminSubmissionExtrasFields form={form} setForm={setForm} sub={sub} update={update} />
                ) : null}

                {section.id === 'reno' ? (
                  <div className="admin-submission-extras__flags admin-submission-extras__flags--solo">
                    <ListingDetailsToggle
                      label="Renovation"
                      value={form.renovationNeeded}
                      onChange={(val) => update('renovationNeeded', val)}
                      id={`admin-sub-sale-renovation-${sub.id}`}
                    />
                    {form.renovationNeeded === 'yes' ? (
                      <label className="admin-submission-content__field admin-submission-content__field--inline admin-submission-extras__cost-inline">
                        <span>Est. cost</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="$"
                          value={form.renovationCost}
                          onChange={(e) => update('renovationCost', filterNumericInput(e.target.value, { decimal: true }))}
                        />
                      </label>
                    ) : null}
                    <ListingDetailsToggle
                      label="Land banking"
                      value={form.landBanking}
                      onChange={(val) => update('landBanking', val)}
                      id={`admin-sub-sale-land-banking-${sub.id}`}
                    />
                  </div>
                ) : null}

                {section.id === 'agent' ? (
                  <div className="admin-submission-content__fields">
                    <div className="admin-submission-content__row">
                      <label className="admin-submission-content__field">
                        <span>Name</span>
                        <input required value={form.agentName} onChange={(e) => update('agentName', e.target.value)} />
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Company</span>
                        <input value={form.agentCompany} onChange={(e) => update('agentCompany', e.target.value)} />
                      </label>
                    </div>
                    <div className="admin-submission-content__row">
                      <label className="admin-submission-content__field">
                        <span>Email</span>
                        <input type="email" required value={form.agentEmail} onChange={(e) => update('agentEmail', e.target.value)} />
                      </label>
                      <label className="admin-submission-content__field">
                        <span>Phone</span>
                        <input value={form.agentPhone} onChange={(e) => update('agentPhone', e.target.value)} />
                      </label>
                    </div>
                    <p className="admin-submission-content__hint">
                      Listing contact email — not the user&apos;s Firebase login. Change account emails under Overview → Registered users.
                    </p>
                  </div>
                ) : null}
              </section>
            ))}
          </div>

          <div className="admin-submission-content__actions">
            <button type="submit" className="admin-submission-content__save-btn" disabled={saving}>
              {saving ? 'Saving…' : 'Save listing details'}
            </button>
          </div>
        </>
      ) : null}
    </form>
  );
};

export default AdminSubmissionContentEditor;
